// Chart scenarios for the course: candles built to show exactly what a lesson teaches. A
// scenario walks through waypoints ([bars from the last waypoint, price]): the turning points
// are the exact highs and lows, so a lesson's levels are exact prices, and nothing between two
// waypoints pokes past them. `shapes` puts a chosen candle at a bar (a hammer at support…).
// The same scenario always gives the same candles.
//
// `volume` gives the candles volume (v), for the volume lessons: { base, runs, at }. A candle's
// volume follows its size (a wide candle trades more), with a little noise; `runs` ([[from, to,
// times]]) scale a stretch of candles (rising volume on the up legs…), and `at` ({ bar: times })
// sets one candle's volume outright as a multiple of `base` (a climax, a no-demand candle…).

// A candle's shape from the close before it: body (signed: up is bullish), upper and lower wicks.
export const SHAPES = {
  hammer: { body: 0.5, upper: 0.25, lower: 3 },
  shootingStar: { body: -0.5, upper: 3, lower: 0.25 },
  doji: { body: 0, upper: 1.5, lower: 1.5, gapUp: 0 },
  bigBull: { body: 4, upper: 0.25, lower: 0.25 },
  bigBear: { body: -4, upper: 0.25, lower: 0.25 },
  smallBull: { body: 0.75, upper: 0.5, lower: 0.5 },
  smallBear: { body: -0.75, upper: 0.5, lower: 0.5 },
  bullEngulf: { body: 3, upper: 0.25, lower: 0.5, open: -0.25 },
  bigBullEngulf: { body: 5, upper: 0.25, lower: 0.5, open: -0.25 },
  bearEngulf: { body: -3, upper: 0.5, lower: 0.25, open: 0.25 },
  insideBar: { body: 0.25, upper: 0.5, lower: 0.5, inside: true },
  newsSpike: { body: -1, upper: 7, lower: 6 },
};

const round = (n, tick) => Math.round(Math.round(n / tick) * tick * 1e6) / 1e6;

export function buildScenario({ start = 5000, tick = 0.25, seed = 1, noise = 1, path = [], shapes = {}, scale = 1, minutes = 5, from = '2026-10-05T13:30:00Z', volume = null }) {
  let s = seed;
  const rand = () => { s = (s * 48271) % 2147483647; return s / 2147483647; };
  const t0 = Math.floor(Date.parse(from) / 1000);
  const bars = [];
  let prev = start;
  let at = start;
  for (const [n, to] of path) {
    const lo = Math.min(at, to);
    const hi = Math.max(at, to);
    for (let k = 1; k <= n; k += 1) {
      const i = bars.length;
      const o = prev;
      let c;
      let h;
      let l;
      const shape = shapes[i] && SHAPES[shapes[i]];
      if (shape) {
        const u = scale;
        const open = o + (shape.open || 0) * u;
        c = open + shape.body * u;
        h = Math.max(open, c) + shape.upper * u;
        l = Math.min(open, c) - shape.lower * u;
        // On a waypoint the candle is moved so its low (at a trough) or high (at a peak) is the waypoint.
        if (k === n) {
          const shift = to < at ? to - l : to - h;
          h += shift; l += shift; c += shift;
          bars.push({ i, t: t0 + i * minutes * 60, o: round(open + shift, tick), h: round(h, tick), l: round(l, tick), c: round(c, tick) });
          prev = c;
          continue;
        }
        if (shape.inside && bars.length) {
          const p = bars[bars.length - 1];
          h = Math.min(h, p.h - tick); l = Math.max(l, p.l + tick);
          c = Math.min(Math.max(c, l), h);
        }
        bars.push({ i, t: t0 + i * minutes * 60, o: round(open, tick), h: round(h, tick), l: round(l, tick), c: round(c, tick) });
        prev = c;
        continue;
      }
      const target = at + (to - at) * (k / n);
      const wiggle = k === n ? 0 : (rand() - 0.5) * noise * scale * 2;
      c = Math.min(hi, Math.max(lo, target + wiggle));
      h = Math.min(hi, Math.max(o, c) + rand() * noise * scale * 0.8);
      l = Math.max(lo, Math.min(o, c) - rand() * noise * scale * 0.8);
      // The waypoint bar reaches the waypoint exactly: a high at a peak, a low at a trough.
      if (k === n) { if (to >= at) h = to; else l = to; }
      bars.push({ i, t: t0 + i * minutes * 60, o: round(o, tick), h: round(Math.max(h, o, c), tick), l: round(Math.min(l, o, c), tick), c: round(c, tick) });
      prev = c;
    }
    // The next leg starts from where this one closed (a shaped candle at a waypoint closes away
    // from its extreme, and the move after it shouldn't swing back past that close).
    at = prev;
  }
  if (volume) addVolume(bars, volume, seed);
  return bars;
}

// Volume for each candle (its own random numbers, so adding volume never changes the candles).
function addVolume(bars, { base = 1000, runs = [], at = {} }, seed) {
  let s = (seed * 7919 + 17) % 2147483647 || 1;
  const rand = () => { s = (s * 48271) % 2147483647; return s / 2147483647; };
  const avg = bars.reduce((sum, b) => sum + (b.h - b.l), 0) / Math.max(1, bars.length) || 1;
  for (const b of bars) {
    const noise = 0.85 + rand() * 0.3;
    if (at[b.i] !== undefined) { b.v = Math.round(base * at[b.i] * (0.95 + (noise - 0.85) / 3)); continue; }
    let v = base * (0.55 + 0.45 * Math.min(2.5, (b.h - b.l) / avg)) * noise;
    for (const [from, to, times] of runs) if (b.i >= from && b.i <= to) v *= times;
    b.v = Math.round(v);
  }
}

// Volume at each price (the volume profile) over `bars`: each candle's volume spread evenly over
// the prices it traded, in rows `step` apart. { rows: [{ low, high, v }] (low to high), poc (the
// row with the most volume), valueLow, valueHigh (the 70% of the volume around the poc) }.
export function volumeProfile(bars, step = 1) {
  const list = bars.filter((b) => Number.isFinite(b.v));
  if (!list.length) return null;
  const lo = Math.floor(Math.min(...list.map((b) => b.l)) / step) * step;
  const hi = Math.ceil(Math.max(...list.map((b) => b.h)) / step) * step;
  const count = Math.max(1, Math.round((hi - lo) / step));
  const rows = Array.from({ length: count }, (_, k) => ({ low: lo + k * step, high: lo + (k + 1) * step, v: 0 }));
  for (const b of list) {
    const from = Math.max(0, Math.floor((b.l - lo) / step));
    const to = Math.min(count - 1, Math.max(from, Math.ceil((b.h - lo) / step) - 1));
    for (let k = from; k <= to; k += 1) rows[k].v += b.v / (to - from + 1);
  }
  let poc = 0;
  rows.forEach((r, k) => { if (r.v > rows[poc].v) poc = k; });
  const total = rows.reduce((sum, r) => sum + r.v, 0);
  let a = poc;
  let z = poc;
  let inside = rows[poc].v;
  while (inside < total * 0.7 && (a > 0 || z < count - 1)) {
    const down = a > 0 ? rows[a - 1].v : -1;
    const up = z < count - 1 ? rows[z + 1].v : -1;
    if (up >= down) { z += 1; inside += up; } else { a -= 1; inside += down; }
  }
  return { rows, poc: rows[poc], valueLow: rows[a].low, valueHigh: rows[z].high };
}

// A line under the candles for the market internals lessons (CourseChart `oscillator` with a
// `path`): NYSE TICK, ADD, VOLD, TRIN or VIX, walked through waypoints like the candles
// ([bars from the last waypoint, value]), with a little noise. `spikes` ({ bar: value }) sets one
// bar outright (a +1,200 TICK…). One value per bar; the last value repeats to fill `length`.
export function buildSeries({ start = 0, path = [], noise = 0, seed = 1, spikes = {} }, length) {
  let s = (seed * 104729 + 7) % 2147483647 || 1;
  const rand = () => { s = (s * 48271) % 2147483647; return s / 2147483647; };
  const out = [];
  let at = start;
  for (const [n, to] of path) {
    for (let k = 1; k <= n; k += 1) {
      const target = at + (to - at) * (k / n);
      out.push(k === n ? to : target + (rand() - 0.5) * noise * 2);
    }
    at = to;
  }
  while (out.length < length) out.push(out.length ? out[out.length - 1] : start);
  out.length = length;
  for (const [i, v] of Object.entries(spikes)) if (Number(i) < length) out[Number(i)] = v;
  return out;
}
